import { useEffect } from 'react';
import { Lightbulb, ClipboardCheck, Bell, Wallet, Star, Bot, ShieldAlert, MessageSquareText } from 'lucide-react';
import { useDashboard } from '../../hooks/useDashboard';
import { useSalesInsights } from '../../hooks/useSalesInsights';
import { MetricCard } from '../ui/MetricCard';
import { ChartCard } from '../ui/ChartCard';
import { Button } from '../ui/Button';
import { HealthGauge } from '../shared/HealthGauge';
import type { DashboardSummary } from '../../api/dashboard';

function formatCurrency(n: number): string {
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function HeroSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="h-[132px] animate-pulse rounded-card border border-border bg-surface" />
      ))}
    </div>
  );
}

function SalesHeroRow() {
  const { snapshots, loading, fetchSnapshots } = useSalesInsights();

  useEffect(() => {
    fetchSnapshots(1);
  }, []);

  if (loading) return <HeroSkeleton />;

  const latest = snapshots[0];

  if (!latest) {
    return (
      <ChartCard
        title="Revenue & orders"
        description="Generate a sales snapshot to see revenue, orders, and average order value from your real Shopify order history."
        actions={<Button size="sm" onClick={() => (window.location.href = '/sales-insights')}>Generate snapshot</Button>}
      >
        <div className="h-4" />
      </ChartCard>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <MetricCard label="Revenue" value={formatCurrency(latest.total_revenue)} />
      <MetricCard label="Orders" value={latest.order_count.toLocaleString()} />
      <MetricCard label="Repeat Customers" value={`${latest.repeat_customer_rate}%`} />
      <MetricCard label="Avg Order Value" value={formatCurrency(latest.average_order_value)} />
    </div>
  );
}

function FeatureActivityRow({ data }: { data: DashboardSummary }) {
  return (
    <ChartCard title="Feature Activity" description="A snapshot across every GrowthPilot feature you're using">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {data.profit_insight ? (
          <MetricCard
            label="Net Profit (latest snapshot)"
            value={formatCurrency(data.profit_insight.net_profit)}
            icon={<Wallet size={18} />}
            growthPct={data.profit_insight.profit_margin_percent}
          />
        ) : (
          <MetricCard label="Net Profit" value="—" icon={<Wallet size={18} />} />
        )}

        <MetricCard
          label="Product Reviews"
          value={data.reviews.approved_count > 0 ? `${data.reviews.average_rating}★ (${data.reviews.approved_count})` : '—'}
          icon={<Star size={18} />}
        />

        <MetricCard
          label="AI Cost (30 days)"
          value={`$${data.ai_usage_30d.total_cost_usd.toFixed(2)}`}
          icon={<Bot size={18} />}
        />

        <MetricCard
          label="Orders Flagged as Risky (30d)"
          value={String(data.fraud_flagged_orders_30d)}
          icon={<ShieldAlert size={18} />}
        />

        <MetricCard
          label="Support Questions Answered (30d)"
          value={String(data.support_questions_30d)}
          icon={<MessageSquareText size={18} />}
        />

        {data.reviews.pending_count > 0 && (
          <MetricCard
            label="Reviews Awaiting Moderation"
            value={String(data.reviews.pending_count)}
            icon={<Star size={18} />}
          />
        )}
      </div>
    </ChartCard>
  );
}

export function DashboardSummaryCards() {
  const { data, isLoading, isError, refetch } = useDashboard();

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <HeroSkeleton />
        <div className="h-[140px] animate-pulse rounded-card border border-border bg-surface" />
      </div>
    );
  }

  if (isError) {
    return (
      <ChartCard title="Dashboard summary">
        <div className="flex items-center justify-between">
          <span className="text-[14px] text-danger">Failed to load your dashboard summary.</span>
          <Button size="sm" variant="secondary" onClick={() => refetch()}>Retry</Button>
        </div>
      </ChartCard>
    );
  }

  if (!data) return null;

  return (
    <div className="flex flex-col gap-4">
      <SalesHeroRow />

      <ChartCard title="Store Health Score">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-col gap-1">
            {data.latest_score === null && (
              <span className="text-[16px] text-text-secondary">No audit yet — run your first store audit to see a score.</span>
            )}
            {data.latest_audit_completed_at && (
              <span className="text-[15px] text-text-secondary">
                Last run {new Date(data.latest_audit_completed_at).toLocaleDateString()}
              </span>
            )}
          </div>
          {data.latest_score !== null && <HealthGauge score={data.latest_score} />}
        </div>
      </ChartCard>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <MetricCard
          label="Actionable Recommendations"
          value={String(data.actionable_recommendations)}
          icon={<Lightbulb size={18} />}
        />
        <MetricCard
          label="Audits This Month"
          value={String(data.audits_used_this_month)}
          icon={<ClipboardCheck size={18} />}
        />
        <MetricCard
          label="Unread Notifications"
          value={String(data.unread_notifications)}
          icon={<Bell size={18} />}
        />
      </div>

      <FeatureActivityRow data={data} />
    </div>
  );
}
